import PropTypes from "prop-types";
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { Link, Slider, Typography, Box, CircularProgress, Tooltip } from "@mui/material";

// Importiamo il core di Swiper v5 vecchio stile
import Swiper from "swiper";
import "swiper/css/swiper.min.css";

// Importiamo react-pageflip per PC
import HTMLFlipBook from "react-pageflip";

import ProductgridList from "./ProductgridList";
import ProductMobile from "../../pages/shop-product/ProductMobile";
import ProductPageCatalog from "../../pages/shop-product/ProductPageCatalog";
import ProductPageCatalogTemplate from "../../pages/shop-product/ProductPageCatalogTemplate";

// Wrapper necessario per react-pageflip (richiede un componente con ref forwardato)
const PageComponent = React.forwardRef((props, ref) => {
  return (
    <div className="page" ref={ref} style={{ width: "100%", height: "100%" }}>
      {props.children}
    </div>
  );
});

PageComponent.displayName = "PageComponent";

const ShopProductsFlip = (props) => {
  const {
    products,
    layout,
    isLoggedUser,
    visualizzaPrezzi,
    oneProduct,
    catalogo,
  } = props;

  const sliderRef = useRef(null);
  const bookRef = useRef(null); // Ref per react-pageflip su PC

  const [currentPage, setCurrentPage] = useState(0);
  const [totPages, setTotPages] = useState(0);

  const [isBookReady, setIsBookReady] = useState(false);
  const [mountBook, setMountBook] = useState(false);
  const [swiperInstance, setSwiperInstance] = useState(null);
  const [hoverPage, setHoverPage] = useState(null);

  const [pagesArray, setPagesArray] = useState([]);
  const [isCalculating, setIsCalculating] = useState(true);
  const [showTutorial, setShowTutorial] = useState(false);

  // Rilevamento iniziale sicuro della visualizzazione mobile
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth < 900 : false);

  const [editDialog, setEditDialog] = useState({
    isOpen: false,
    id: null,
    currentQty: 0,
    min: 0,
    conf: 1,
  });

  const RENDER_BUFFER = 6;

  // Gestione del ridimensionamento dello schermo stabile
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 900);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getNumOfVariants = useCallback((product, template) => {
    if (!product || !product.variation) return 0;

    if (template === "grid_with_sizes") {
      const groupedVariants = product.variation.map((v, indexColor) => {
        return {
          colorId: `c-${indexColor}`,
          color: v.color || `Variante ${indexColor + 1}`,
          sizes: (v.size && v.size.length > 0) ? v.size.map((sizeItem, indexSize) => {
            return {
              id: `${indexColor}-${indexSize}`,
              sizeLabel: sizeItem.name || sizeItem.label || "Unica",
              quantity: 0,
              price: sizeItem["pricesco"] || sizeItem.pricesco || "0.00",
              src: global.baseUrlImage + sizeItem.nomImg + "?t=" + Math.random(),
              nomImg: sizeItem.skuSizeColor,
            };
          }) : []
        };
      });
      return groupedVariants.length;
    }

    const initialVariants = product?.variation?.flatMap((v, indexColor) => {
      if (!v || !v.size || v.size.length === 0) return [];
      return v.size.map((sizeItem, indexSize) => {
        return {
          id: `${indexColor}-${indexSize}`,
          color: v.colore || `Variante ${indexColor + 1}`,
          quantity: 0,
          price: sizeItem["pricesco"] || sizeItem.pricesco || "0.00",
          src: global.baseUrlImage + sizeItem.nomImg + "?t=" + Math.random(),
          nomImg: sizeItem.skuSizeColor,
        };
      });
    });
    return initialVariants.length;
  }, []);

  useEffect(() => {
    setIsCalculating(true);

    const timerId = setTimeout(() => {
      let globalIndex = 0;
      const result = [];

      (catalogo || []).forEach((pagina, outerIndex) => {
        if (
          (pagina.template === "grid" || pagina.template === "grid_with_sizes") &&
          (!pagina.IDArtBas || pagina.IDArtBas === 0)
        ) {
          return;
        }

        if (pagina.template === "end") {
          result.push({
            isEnd: true,
            key: `end-${outerIndex}`,
            index: globalIndex++,
          });
          return;
        }

        const product = products.find((p) => p.id === pagina.IDArtBas);
        const loopCount =
          pagina.numPagine &&
            pagina.numPagine > 1 &&
            (pagina.template === "grid" || pagina.template === "grid_with_sizes")
            ? pagina.numPagine
            : 1;

        const numVariants = getNumOfVariants(product, pagina.template);
        const offsetPerPage =
          pagina.numPagine && pagina.numPagine > 1
            ? Math.ceil(numVariants / pagina.numPagine)
            : numVariants;

        for (let innerIndex = 0; innerIndex < loopCount; innerIndex++) {
          const offset = innerIndex * offsetPerPage;
          result.push({
            isEnd: false,
            key: `page-${outerIndex}-${innerIndex}`,
            pagina: { ...pagina, ord: pagina.ord + innerIndex },
            product,
            offsetPerPage,
            offset,
            index: globalIndex++,
          });
        }
      });

      if (result.length === 0 || !result[result.length - 1].isEnd) {
        result.push({
          isEnd: true,
          key: `static-last-page-catalogo`,
          index: globalIndex++,
        });
      }

      setPagesArray(result);
      setIsCalculating(false);
    }, 50);

    return () => clearTimeout(timerId);
  }, [catalogo, products, getNumOfVariants]);

  // CONFIGURAZIONE CORRETTA E RISCRITTA SECONDO LE SPECIFICHE INTERNE DI SWIPER v5
  const swiperParams = useMemo(() => {
    return {
      direction: "horizontal",
      effect: "slide",
      slidesPerView: 1,
      slidesPerGroup: 1,
      spaceBetween: 10,
      allowTouchMove: true,
      grabCursor: true,
      touchStartPreventDefault: false,
      iOSEdgeSwipeDetection: true,
      observer: true,
      observeParents: true,
      observeSlideChildren: true,
      updateOnWindowResize: true,
      speed: 300,
      keyboard: {
        enabled: true,
      }
    };
  }, []);

  useEffect(() => {
    setMountBook(false);
    setIsBookReady(false);

    if (swiperInstance) {
      swiperInstance.destroy(true, true);
      setSwiperInstance(null);
    }

    const timer = setTimeout(() => {
      setMountBook(true);
    }, 300);

    return () => clearTimeout(timer);
  }, [catalogo, isMobile]);

  useEffect(() => {
    if (!mountBook || isCalculating) return;

    const timer = setTimeout(() => {
      if (pagesArray.length > 0) {
        setTotPages(pagesArray.length - 1);
        setIsBookReady(true);
      } else {
        setIsBookReady(true);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [pagesArray, mountBook, isCalculating]);

  // Gestione Tutorial Mobile
  useEffect(() => {
    if (isMobile && isBookReady && pagesArray.length > 0) {
      const hasSeen = false//sessionStorage.getItem("hasSeenFlipTutorial");
      if (!hasSeen) {
        setShowTutorial(true);
        sessionStorage.setItem("hasSeenFlipTutorial", "true");
        // Scompare da solo dopo 4 secondi
        const timer = setTimeout(() => {
          setShowTutorial(false);
        }, 4000);
        return () => clearTimeout(timer);
      }
    }
  }, [isMobile, isBookReady, pagesArray.length]);

  useEffect(() => {
    if (currentPage > 0 && showTutorial) {
      setShowTutorial(false); // Scompare immediatamente al primo swipe (cambio pagina)
    }
  }, [currentPage, showTutorial]);

  // Inizializzazione Swiper pulita (Corregge il bug TypeError 'speed')
  useEffect(() => {
    let swiperVuoto = null;

    if (isMobile && mountBook && isBookReady && !swiperInstance && pagesArray.length > 0) {
      // Passiamo l'oggetto pulito direttamente evitando destructuring errati nel costruttore v5
      swiperVuoto = new Swiper(".swiper-container", {
        direction: swiperParams.direction,
        effect: swiperParams.effect,
        slidesPerView: swiperParams.slidesPerView,
        slidesPerGroup: swiperParams.slidesPerGroup,
        spaceBetween: swiperParams.spaceBetween,
        allowTouchMove: swiperParams.allowTouchMove,
        grabCursor: swiperParams.grabCursor,
        touchStartPreventDefault: swiperParams.touchStartPreventDefault,
        iOSEdgeSwipeDetection: swiperParams.iOSEdgeSwipeDetection,
        observer: swiperParams.observer,
        observeParents: swiperParams.observeParents,
        observeSlideChildren: swiperParams.observeSlideChildren,
        updateOnWindowResize: swiperParams.updateOnWindowResize,
        speed: swiperParams.speed,
        keyboard: swiperParams.keyboard,
        on: {
          slideChange: function () {
            setCurrentPage(this.activeIndex);
          }
        },
      });

      setSwiperInstance(swiperVuoto);

      setTimeout(() => {
        if (swiperVuoto && !swiperVuoto.destroyed) {
          swiperVuoto.update();
        }
      }, 500);
    }

    return () => {
      if (swiperVuoto) {
        swiperVuoto.destroy(true, true);
        setSwiperInstance(null);
      }
    };
  }, [mountBook, isBookReady, pagesArray, swiperParams, isMobile]);

  const handleSliderChange = (event, newValue) => {
    setCurrentPage(newValue);
  };

  const handleSliderChangeCommitted = (event, newValue) => {
    if (isMobile && swiperInstance) {
      swiperInstance.slideTo(newValue);
    } else if (!isMobile && bookRef.current) {
      bookRef.current.pageFlip().turnToPage(newValue);
    }
    setCurrentPage(newValue);
  };

  const handleSliderMouseMove = (e) => {
    if (totPages <= 0 || !sliderRef.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    let percent = (e.clientX - rect.left) / rect.width;
    percent = Math.max(0, Math.min(1, percent));
    const value = Math.round(percent * totPages);
    setHoverPage((prev) => (prev !== value ? value : prev));
  };

  const getPageLabel = (pageIndex) => {
    if (pageIndex === null || !pagesArray || !pagesArray[pageIndex]) return "";
    const p = pagesArray[pageIndex];
    if (p.isEnd) return "Fine Catalogo";
    if (p.pagina && p.pagina.ord !== undefined) return p.pagina.ord;
    return pageIndex + 1;
  };

  const clickData = useRef({ x: 0, y: 0, time: 0 });

  const handlePointerDown = useCallback((e) => {
    let clientX = e.clientX;
    let clientY = e.clientY;
    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    }

    clickData.current = {
      x: clientX,
      y: clientY,
      time: Date.now()
    };
  }, []);

  const handlePointerUp = useCallback((e) => {
    // Ignora se stiamo interagendo con elementi cliccabili interni (bottoni, input)
    if (e.target.closest('button') || e.target.closest('input') || e.target.closest('.qty-controls')) {
      return;
    }

    let clientX = e.clientX;
    let clientY = e.clientY;
    if (e.changedTouches && e.changedTouches.length > 0) {
      clientX = e.changedTouches[0].clientX;
      clientY = e.changedTouches[0].clientY;
    }

    if (clientX === undefined || clientY === undefined) return;

    const dx = Math.abs(clientX - clickData.current.x);
    const dy = Math.abs(clientY - clickData.current.y);
    const dt = Date.now() - clickData.current.time;

    // Se è uno swipe/drag o una pressione prolungata, ignoriamo (viene gestito dal drag nativo)
    if (dx > 10 || dy > 10 || dt > 500) {
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const x = clientX - rect.left;
    const EDGE_MARGIN = 150; // Margine di 150px sui bordi abilitato allo sfoglio tramite click

    // Sfoglia indietro se cliccato a sinistra, avanti se a destra. Nulla al centro.
    if (x < EDGE_MARGIN) {
      if (bookRef.current) bookRef.current.pageFlip().flipPrev();
    } else if (x > rect.width - EDGE_MARGIN) {
      if (bookRef.current) bookRef.current.pageFlip().flipNext();
    }
  }, []);

  const handleDialogSave = () => {
    setEditDialog({ ...editDialog, isOpen: false });
    if (editDialog.currentQty - editDialog.prevQty === 0) return;
    editDialog.updateQuantity(null, editDialog.id, editDialog.currentQty - editDialog.prevQty, editDialog.min, editDialog.conf, editDialog.pzBox, editDialog.preBox)
  };

  const handlePrev = () => {
    if (isMobile && swiperInstance) {
      swiperInstance.slidePrev();
    } else if (!isMobile && bookRef.current) {
      bookRef.current.pageFlip().flipPrev();
    }
  };

  const handleNext = () => {
    if (isMobile && swiperInstance) {
      swiperInstance.slideNext();
    } else if (!isMobile && bookRef.current) {
      bookRef.current.pageFlip().flipNext();
    }
  };

  const onPageFlipEvent = (e) => {
    setCurrentPage(e.data);
  };

  return (
    <div
      className="shop-bottom-area mt-4"
      id="products-area"
      style={{
        position: "relative",
        width: "100%",
        maxWidth: "100vw",
        margin: "0 auto",
        minHeight: "500px",
        padding: "0 10px"
      }}
    >
      <style>{`
        .swiper-container {
          width: 100% !important;
          height: 100% !important;
          overflow: hidden !important; 
          touch-action: pan-y !important;
        }
        .swiper-wrapper {
          display: flex !important;
          flex-direction: row !important;
        }
        .swiper-slide {
          width: 100%;
          flex-shrink: 0 !important;
          height: auto !important;
        }
        @media (min-width: 900px) {
          .page-flip-container {
            display: flex;
            justifyContent: center;
            align-items: center;
            width: 100%;
          }
          .page:nth-child(odd) .demoPage {
            box-shadow: inset -15px 0 20px -15px rgba(0, 0, 0, 0.2);
            border-right: 1px solid #eaeaea;
          }
          .page:nth-child(even) .demoPage {
            box-shadow: inset 15px 0 20px -15px rgba(0, 0, 0, 0.2);
            border-left: 1px solid #eaeaea;
          }
        }
        @keyframes swipeHand {
          0% { transform: translateX(30px); opacity: 0; }
          20% { opacity: 1; }
          80% { opacity: 1; }
          100% { transform: translateX(-30px); opacity: 0; }
        }
      `}</style>

      {/* TUTORIAL OVERLAY MOBILE */}
      {showTutorial && isMobile && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.65)",
            zIndex: 1000,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            pointerEvents: "none", /* Fondamentale: permette allo swipe di passare oltre l'overlay! */
            borderRadius: "8px"
          }}
        >
          <div style={{ fontSize: "4rem", animation: "swipeHand 2s infinite" }}>
            👆
          </div>
          <h3 style={{ color: "white", marginTop: "20px", textAlign: "center", padding: "0 20px", fontWeight: "bold", textShadow: "0 2px 4px rgba(0,0,0,0.5)" }}>
            Scorri a destra o sinistra<br />per sfogliare il catalogo
          </h3>
        </div>
      )}

      {/* MODALE QUANTITÀ */}
      {editDialog.isOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 99999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setEditDialog({ ...editDialog, isOpen: false })}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: "white",
              padding: "2rem",
              borderRadius: "12px",
              boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              width: "90%",
              maxWidth: "320px",
              textAlign: "center",
            }}
          >
            <h4>Modifica Quantità</h4>
            <input
              type="number"
              autoFocus
              value={editDialog.currentQty}
              onChange={(e) =>
                setEditDialog({ ...editDialog, currentQty: parseInt(e.target.value) || 0 })
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") handleDialogSave();
              }}
              style={{
                fontSize: "2rem",
                padding: "10px",
                textAlign: "center",
                border: "2px solid #ddd",
                borderRadius: "8px",
                width: "100%",
              }}
            />
            <div style={{ display: "flex", gap: "10px", justifyContent: "center", marginTop: "10px" }}>
              <button
                onClick={() => setEditDialog({ ...editDialog, isOpen: false })}
                className="btn btn-secondary"
                style={{ padding: "10px 20px", border: "1px solid #ccc", background: "#f0f0f0", borderRadius: "5px", flex: 1 }}
              >
                Annulla
              </button>
              <button
                onClick={handleDialogSave}
                className="btn btn-primary"
                style={{ padding: "10px 20px", background: "#007bff", color: "white", border: "none", borderRadius: "5px", flex: 1 }}
              >
                Conferma
              </button>
            </div>
          </div>
        </div>
      )}

      {/* LOADER */}
      {(!isBookReady || isCalculating) && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#ffffff",
            zIndex: 10,
          }}
        >
          <CircularProgress size={50} />
          <Typography variant="h6" sx={{ mt: 2, color: "#555" }}>
            {isCalculating ? "Elaborazione dati in corso..." : "Preparazione del catalogo..."}
          </Typography>
        </Box>
      )}

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          opacity: isBookReady ? 1 : 0,
          pointerEvents: isBookReady ? "auto" : "none",
          transition: "opacity 0.3s ease-in-out",
          width: "100%"
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
          {/* Frecce visibili in modalità Desktop */}
          {currentPage > 0 && (
            <div className="d-none d-lg-block shopping-cart-btn btn-hover text-center" style={{ marginRight: "10px" }}>
              <button
                className="default-btn"
                onClick={handlePrev}
                style={{ height: "120px", display: "flex", alignItems: "center", border: "none", padding: "0 15px", background: "#f5f5f5", borderRadius: "6px", cursor: "pointer" }}
              >
                &lt;
              </button>
            </div>
          )}

          {mountBook && (
            <div style={{ width: "100%", maxWidth: "1200px", overflow: "hidden" }}>
              {isMobile ? (
                /* RENDERING MOBILE: SWIPER BLINDATO */
                <div className="swiper-container">
                  <div className="swiper-wrapper">
                    {pagesArray.map((pageConf) => {
                      const isVisible = Math.abs(pageConf.index - currentPage) <= RENDER_BUFFER;
                      return (
                        <div className="swiper-slide" key={pageConf.key}>
                          <div
                            className="demoPage"
                            style={{ backgroundColor: "#fff", padding: "5px", boxSizing: "border-box", height: "100%", minHeight: "500px" }}
                          >
                            {isVisible ? (
                              pageConf.isEnd ? (
                                <div style={{ textAlign: "center", padding: "50px 20px" }}>
                                  <img src={global.placeholderImage} alt="Logo" style={{ maxWidth: "150px", marginBottom: "20px" }} />
                                  <h2>Fine del Catalogo</h2>
                                  <p className="mb-4">Scopri la nostra collezione tessile</p>
                                  <div className="shopping-cart-btn btn-hover text-center">
                                    <a href="/shop-grid-full-width/show=GRAFICHE" className="default-btn" style={{ display: "inline-block" }}>Vai al Tessile</a>
                                  </div>
                                </div>
                              ) : pageConf.product ? (
                                <ProductPageCatalogTemplate
                                  isLoggedUser={isLoggedUser}
                                  visualizzaPrezzi={visualizzaPrezzi}
                                  pagina={pageConf.pagina}
                                  product={pageConf.product}
                                  notShowGridAvailability={true}
                                  showCatalogo={true}
                                  catalogoTemplate={true}
                                  offsetPerPage={pageConf.offsetPerPage}
                                  variantOffset={pageConf.offset}
                                  setEditDialog={setEditDialog}
                                  editDialog={editDialog}
                                />
                              ) : null
                            ) : (
                              <div style={{ width: "100%", height: "350px" }}></div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                /* RENDERING PC DESKTOP: REACT-PAGEFLIP VERO EFFETTO SFOGLIO */
                <div className="page-flip-container">
                  <HTMLFlipBook
                    width={550}
                    height={750}
                    size="stretch"
                    minWidth={400}
                    maxWidth={800}
                    minHeight={500}
                    maxHeight={1000}
                    maxShadowOpacity={0.5}
                    showCover={false}
                    disableFlipByClick={true}
                    onFlip={onPageFlipEvent}
                    ref={bookRef}
                    className="flipbook-desktop"
                  >
                    {pagesArray.map((pageConf) => {
                      return (
                        <PageComponent key={pageConf.key}>
                          <div
                            className="demoPage"
                            style={{ backgroundColor: "#fff", padding: "20px", boxSizing: "border-box", height: "100%", border: "1px solid #f0f0f0" }}
                            onPointerDown={handlePointerDown}
                            onPointerUp={handlePointerUp}
                          >
                            {pageConf.isEnd ? (
                              <div style={{ textAlign: "center", padding: "100px 20px" }}>
                                <img src={global.placeholderImage} alt="Logo" style={{ maxWidth: "150px", marginBottom: "20px" }} />
                                <h2>Fine del Catalogo</h2>
                                <p className="mb-4">Scopri la nostra collezione tessile</p>
                                <div className="shopping-cart-btn btn-hover text-center">
                                  <a href="/shop-grid-full-width/show=GRAFICHE" className="default-btn" style={{ display: "inline-block" }}>Vai al Tessile</a>
                                </div>
                              </div>
                            ) : pageConf.product ? (
                              <ProductPageCatalogTemplate
                                isLoggedUser={isLoggedUser}
                                visualizzaPrezzi={visualizzaPrezzi}
                                pagina={pageConf.pagina}
                                product={pageConf.product}
                                notShowGridAvailability={true}
                                showCatalogo={true}
                                catalogoTemplate={true}
                                offsetPerPage={pageConf.offsetPerPage}
                                variantOffset={pageConf.offset}
                                setEditDialog={setEditDialog}
                                editDialog={editDialog}
                              />
                            ) : null}
                          </div>
                        </PageComponent>
                      );
                    })}
                  </HTMLFlipBook>
                </div>
              )}
            </div>
          )}

          {currentPage < totPages && (
            <div className="d-none d-lg-block shopping-cart-btn btn-hover text-center" style={{ marginLeft: "10px" }}>
              <button
                className="default-btn"
                onClick={handleNext}
                style={{ height: "120px", display: "flex", alignItems: "center", border: "none", padding: "0 15px", background: "#f5f5f5", borderRadius: "6px", cursor: "pointer" }}
              >
                &gt;
              </button>
            </div>
          )}
        </div>

        {/* CONTROLLI SLIDER */}
        <Box sx={{ width: "100%", maxWidth: 320, mt: 3, display: "flex", flexDirection: "column", alignItems: "center", px: 2 }}>
          <Typography variant="caption" display="block" gutterBottom sx={{ fontWeight: 600 }}>
            Pagina {getPageLabel(currentPage)}
          </Typography>

          <Tooltip
            title={hoverPage !== null ? `Vai a pagina ${getPageLabel(hoverPage)}` : ""}
            placement="top"
            followCursor
          >
            <Box sx={{ width: "100%" }} onMouseMove={handleSliderMouseMove} onMouseLeave={() => setHoverPage(null)}>
              <Slider
                ref={sliderRef}
                size="small"
                value={currentPage}
                min={0}
                max={totPages}
                onChange={handleSliderChange}
                onChangeCommitted={handleSliderChangeCommitted}
                aria-label="Pagine Catalogo"
                valueLabelDisplay="off"
              />
            </Box>
          </Tooltip>
        </Box>
      </div>
    </div>
  );
};

ShopProductsFlip.propTypes = {
  layout: PropTypes.string,
  catalogo: PropTypes.array,
};

export default ShopProductsFlip;